<template>
    <wrapper-features-templates-one-column
    buttonFullWidth
    noWhiteBox
    :customBackgroundImage="applyErrorState || showLoader || showEmptyState || !getMobileAppState ? null : 'linear-gradient(var(--secondary-light-3) 400px, var(--secondary-light-4) 5%)'"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :errorStateMessage="errorStateMessage"
    :showFooter="!!footerButtonsParams.filter(obj => !obj.hidden).length"
    :footerButtonsParams="footerButtonsParams.filter(obj => !obj.hidden)"
    :showTopRightAreaSlot="showSelector && !showEmptyStateError && !showEmptyState"
    @backOrCloseClicked="emit('backOrCloseClicked')"
    @customTabClicked="tabClicked">
        <!-- *********************************************
        *                 Account Selector               *
        ********************************************** -->
        <template #accounts-card-slot-area>
            <credit-selector
            :class="{ 'mobile-margin': getMobileAppState }"
            :selectedOption="selectedAccount.id"
            :options="getAccountsList"
            :creditInfo="creditInfo"
            :spinnerActive="selectorsWithDetailsLoader"
            @clicked="selectAccountHandler"/>
        </template>
        <!-- *********************************************
        *                   Credit Resume                *
        ********************************************** -->
        <credit-resume
        v-if="getAccountsList.length"
        style="margin-bottom: 16px;"
        :creditInfo="getFinancialPlan.creditAccountExtraInfo"
        :currency="selectedAccount?.currency"
        :isMobile="getMobileAppState"
        :spinnerActive="selectorsWithDetailsLoader"/>

        <!-- Tabs -->
        <tabs-generic v-if="getAccountsList.length" class="tabs-container prevent-auto-margins" :tabsList="tabs" @clicked="tabClicked"/>
        <!-- White box for content wrapper -->
        <div
        v-if="!applyErrorState && !showLoader && getAccountsList.length"
        class="white-content-box prevent-auto-margins"
        :class="{ 'mobile': getMobileAppState, 'no-paddings': selectedTabId === 1 }">
            <!-- *********************************************
            *                  Visible Filters               *
            ********************************************** -->
            <consult-filters-wrapper
            v-if="showFilters && selectedTabId === 2"
            :filterTags="filterTags"
            :applyButtonDisabled="applyButtonDisabled"
            @applyFilters="applyFilters(appliedFilters)"
            @removeFilterTag="removeFilterTag"
            @closeAllFilters="closeAllFilters">
                <!-- Double Calendar -->
                <input-double-calendar
                calendarDirection="row"
                :fromDateParams="beginDateParams"
                :toDateParams="endDateParams"
                @inputFromDate="appliedFilters.beginDate = $event"
                @inputToDate="appliedFilters.endDate = $event"/>
            </consult-filters-wrapper>
            <transition name="common-enter-animation" mode="out-in" appear>
                <div v-if="!showLoader && !applyErrorState && !showEmptyState" class="tabs-content">
                    <!-- Tab  -->
                    <component
                    :is="tabContentComponent"
                    :financialPlanList="getFinancialPlanList"
                    :currency="selectedAccount.currency"
                    :accountId="selectedAccount.id"
                    :informationsList="accountInformations"
                    :creditAccountExtraInfo="getFinancialPlan.creditAccountExtraInfo"
                    :selectedAccount="selectedAccount.id"
                    :tableLoadingMore="tableLoadingMore"
                    :hasFilters="!!filterTags.length"
                    @loadMoreMovements="loadMoreMovements"
                    @loadMoreFinancialPlanList="loadMoreFinancialPlanList"/>
                </div>
            </transition>
        </div>
        <!-- *********************************************
        *                    Empty State                 *
        ********************************************** -->
		<template v-if="!showLoader && !applyErrorState && showEmptyState" #empty-state-slot>
            <empty-state-generic
            illustration="emptyState/statement"
            :title="t('credits.myCredits.emptyStates.generics.title')"
            :description="t('credits.myCredits.emptyStates.generics.description')"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Feedback Toast -->
    <toasts-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import CreditResume from '@/components/features/credits/myCredits/CreditResume'
import CreditSelector from '@/components/generic/selectorsWithDetails/Credit'
import TabsGeneric from '@/components/generic/tabs/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* Aux */
import moment from '@/assets/setups/moment'
import { getDate, createJSONDateEndDay } from '@/assets/scripts/dateBuilders'
import { clearObject } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { getMobileAppState } = mapGetters()
const {	accountsListFetch, financialPlanListFetch, movimentsFetch, accountInformationsRequest, clearFeatureStates } = mapActions(moduleMapper.CREDITS_MY_CREDITS)
const { getAccountsList, getFinancialPlan, getFinancialPlanList, getLastMovementInfo, getMovementsList } = mapGetters(moduleMapper.CREDITS_MY_CREDITS)

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])
/* Show Data */
const showLoader = ref(false)
const tableLoadingMore = ref(false)
const showSelector = ref(false)
const showFilterButton = ref(false)
const showEmptyStateError = ref(false)
const showEmptyState = ref(false)

/*************************************************
*       Selectors With Details Credit Data       *
*************************************************/
const selectedAccount = ref()
const selectorsWithDetailsLoader = ref(false)
const creditInfo = ref({})

const selectAccountHandler = $event => {
    selectedAccount.value = getAccountsList.value.find(account => account.id === $event)
    tabClicked(1)
}

/*************************************************
*                                                *
*                 Tabs Generics                  *
*                                                *
*************************************************/
const disabledTabs = ref(false)
const tabs = ref([
	{ id: 1, label: t('credits.myCredits.tabs.installments'), selected: true, disabled: true },
	{ id: 2, label: t('credits.myCredits.tabs.movements'), selected: false, disabled: true },
	{ id: 3, label: t('credits.myCredits.tabs.informations'), selected: false, disabled: true }
])

const tabClicked = $event => {
    tabs.value.forEach(tab => {
		tab.selected = tab.id === $event
	})
    /* We want to reset filters if we change tabs, and then request tab content */
    filterTags.value = []
    financialPlanMaturityDate.value = null
    resetPaginationParams()
    showFilterButton.value = false
    consultTabMapper[selectedTabId.value - 1].request()
}

const selectedTabId = computed(() => {
    return tabs.value.find(obj => obj.selected).id
})

/* Tab Content Component */
const tabContentComponent = computed(() => {
    const selectedTab = consultTabMapper[selectedTabId.value - 1].tab
    return defineAsyncComponent(() => import('@/components/features/credits/myCredits/' + selectedTab))
})

/*************************************************
*                                                *
*               Initial  Requests                *
*                                                *
*************************************************/
const initialRequest = async () => {
	showLoader.value = true
    applyErrorState.value = false
    disabledTabs.value = true
    showFilters.value = false

    try {
        await accountsListFetch()

        if (!getAccountsList.value.length) {
            showLoader.value = false
            showEmptyState.value = true
            return
        }

        tabs.value = tabs.value.map(tab => {
            return { ...tab, disabled: false }
        })
        selectedAccount.value = getAccountsList.value.find(account => account.default)
        showSelector.value = true
        showFilters.value = true

        await installmentsRequest()
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        showEmptyStateError.value = true
        errorHandler(error.response, initialRequest)
	}
}

/*************************************************
*                                                *
*               Installments Fetch               *
*                                                *
*************************************************/
const financialPlanMaturityDate = ref(null)

const installmentsRequest = async () => {
    cancelAllPendingRequests()

    disabledTabs.value = true

    financialPlanMaturityDate.value ? tableLoadingMore.value = true : showLoader.value = true
    applyErrorState.value = false
    selectorsWithDetailsLoader.value = true

    const payloadFinancialPlanList = {
        params: {
            accountId: selectedAccount.value.id,
            numberOfRecords: 20,
            maturityDate: financialPlanMaturityDate.value ? moment(financialPlanMaturityDate.value).add(1, 'M').toJSON() : null
        }
    }

	try {
        await financialPlanListFetch(payloadFinancialPlanList)
        /* Setting info for selector with details */
        creditInfo.value = {
            id: 1,
            loanInitialValue: Math.abs(getFinancialPlan.value.creditAccountExtraInfo.loanInitialValue),
            loanLeftToPay: Math.abs(getFinancialPlan.value.creditAccountExtraInfo.loanLeftToPay),
            accountCurrency: selectedAccount.value.currency,
            numberOfRemainingInstallments: getFinancialPlan.value.creditAccountExtraInfo.numberOfRemainingInstallments,
            loanInterestRate: getFinancialPlan.value.creditAccountExtraInfo.loanInterestRate,
            financialPlan: getFinancialPlan.value.financialPlan
        }
        showLoader.value = false
        tableLoadingMore.value = false
        disabledTabs.value = false
        selectorsWithDetailsLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        if (!financialPlanMaturityDate.value) applyErrorState.value = true
		errorHandler(error.response, installmentsRequest)
	}
}

const loadMoreFinancialPlanList = () => {
    financialPlanMaturityDate.value = getFinancialPlanList.value[getFinancialPlanList.value.length - 1].maturityExpiration
    installmentsRequest()
}
/*************************************************
*                                                *
*                Moviments Fetch                 *
*                                                *
*************************************************/
const paginationParams = ref({})

const resetPaginationParams = () => {
    paginationParams.value = {
        pageNumber: 1,
        orderNumberPosition: '',
        releaseDatePosition: ''
    }
}

const movimentsRequest = async () => {
    cancelAllPendingRequests()

    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true
    applyErrorState.value = false

    const movementsPayload = {
        accountId: selectedAccount.value.id,
        params: {
            ...appliedFilters.value,
            ...paginationParams.value,
            endDate: createJSONDateEndDay(appliedFilters.value.endDate)
        }
	}

	try {
        await movimentsFetch(movementsPayload)
        if (getMovementsList.value.length > 0) showFilterButton.value = true
        showLoader.value = false
        tableLoadingMore.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        if (paginationParams.value.pageNumber === 1) applyErrorState.value = true
		errorHandler(error.response, movimentsRequest)
	}
}

const loadMoreMovements = () => {
    paginationParams.value.pageNumber++
    paginationParams.value.orderNumberPosition = getLastMovementInfo.value.orderNumberPosition
    paginationParams.value.releaseDatePosition = getLastMovementInfo.value.releaseDatePosition
    movimentsRequest()
}
/*************************************************
*                                                *
*             Informations Request               *
*                                                *
*************************************************/
const accountInformations = ref(null)
const informationsRequest = async () => {
    cancelAllPendingRequests()

	showLoader.value = true
    applyErrorState.value = false

    const informationsPayload = selectedAccount.value.id

	try {
        const response = await accountInformationsRequest(informationsPayload)
		accountInformations.value = response.data.data.lineGroups

        showSelector.value = true
		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response, informationsRequest)
	}
}

/*************************************************
*                                                *
*                  Filter Modal                  *
*                                                *
*************************************************/
const showFilters = ref(false)
const filterTags = ref([])
const appliedFilters = ref({
	beginDate: '',
	endDate: ''
})

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('credits.myCredits.movements.modal.filters.fromDateParams.labelText'),
        placeholder: t('credits.myCredits.movements.modal.filters.fromDateParams.labelText'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
	return {
        labelText: t('credits.myCredits.movements.modal.filters.toDateParams.labelText'),
        placeholder: t('credits.myCredits.movements.modal.filters.toDateParams.labelText'),
        inputValue: appliedFilters.value.endDate
    }
})

const applyButtonDisabled = computed(() => {
    return !appliedFilters.value.beginDate && !appliedFilters.value.endDate
})

const applyFilters = (filters) => {
    filterTags.value = []
    appliedFilters.value = filters

    for (const filter in filters) {
		if (filters[filter]) {
            filterTags.value.push({
                id: filter,
                label: t(`credits.myCredits.movements.modal.filters.tag.${filter}`),
                text: getDate(filters[filter])
            })
        } else {
			appliedFilters.value[filter] = ''
		}
	}
    resetPaginationParams()
    movimentsRequest()
}

const removeFilterTag = (tagId) => {
    appliedFilters.value[tagId] = ''
	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
    resetPaginationParams()
    movimentsRequest()
}

const closeAllFilters = () => {
    filterTags.value = []
    appliedFilters.value = clearObject(appliedFilters.value)
    resetPaginationParams()
    movimentsRequest()
}

/*************************************************
*                                                *
*                 Error Feedbacks                *
*                                                *
*************************************************/
const applyErrorState = ref(false)
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    tableLoadingMore.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

const errorStateMessage = computed(() => {
    return showSelector.value ? tabs.value.find(obj => obj.selected).message : t('credits.myCredits.emptyStates.error.message')
})

/* Mapping credit tab views */
const consultTabMapper = [
    { tab: 'installments/Installments', message: t('credits.myCredits.emptyStates.error.installments'), request: installmentsRequest },
    { tab: 'movements/Movements', message: t('credits.myCredits.emptyStates.error.movements'), request: movimentsRequest },
    { tab: 'informations/Informations', message: t('credits.myCredits.emptyStates.error.informations'), request: informationsRequest }
]

/* Footer Buttons Data */
const footerButtonsParams = computed(() => {
    if (!selectedAccount.value) return []

    return [{
        label: t('credits.myCredits.amortizeBtn'),
        callbackFunction: () => { navigateByName(routeList.CREDITS_MY_CREDITS_AMORTIZATION_NAME, { selectedCreditAccount: selectedAccount.value, creditInfo: creditInfo.value }) },
        hidden: !selectedAccount.value.canBeSettled,
        buttonType: 'primary'
    }]
})

initialRequest()
resetPaginationParams()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.white-content-box {
    @include cardGeneral1;
    margin-top: 16px;

    &.mobile {
        padding: 16px 0;

        &.no-paddings {
            padding: 0px;
        }

        .visible-filters-wrapper {
            padding-left: 16px;
            padding-right: 16px;
        }
    }

}

.tabs-content {
    @include commonEnteringAnimation;
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
